import * as React from "react"
import { AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { InsertConditionDialog } from "@/components/automations/InsertConditionDialog"
import { nonGsmCharacters } from "@/lib/gsm7"
import { insertAtCursor } from "@/lib/insert-at-cursor"
import {
  MAX_RAW_MESSAGE_LENGTH,
  longestFixedLength,
  parseMessageTemplate,
  unknownFieldErrors,
  variablesUsed,
} from "@/lib/message-template"
import { messageVariables } from "@/lib/render-message"
import { MESSAGE_LIMIT } from "@/types/automation"

/**
 * Whether a message would be refused on save: a malformed condition, one
 * testing a column the query does not return, a version whose fixed text alone
 * is longer than the limit, or syntax past the raw cap. The server refuses the same things; checking here
 * saves the round trip.
 */
export function messageBlocksSave(value: string, variables: string[]): boolean {
  const template = parseMessageTemplate(value)
  if (!template.valid) return true
  if (Array.from(value).length > MAX_RAW_MESSAGE_LENGTH) return true
  if (longestFixedLength(template) > MESSAGE_LIMIT) return true
  return variables.length > 0 && unknownFieldErrors(template, variables).length > 0
}

/**
 * The one message editor: the admin template's default wording, and each
 * organization's own. Milestone 24 renders one of these per language.
 *
 * Variable chips and the Insert condition helper insert at the cursor rather
 * than at the end, so either can go mid-sentence. The counter judges the
 * longest version's fixed text: the names and other details are added member
 * by member, measured when each text is rendered, and the setup preview says
 * how close the real texts come (milestone 32).
 */
export function MessageEditor({
  id = "custom_message",
  label = "Message",
  value,
  variables,
  error,
  onChange,
  resetTo,
  hint,
  description,
  unknownVariableNote = "isn’t something this automation knows about, so it will come out blank.",
  readOnly = false,
}: {
  id?: string
  label?: string
  value: string
  variables: string[]
  error?: string
  onChange: (value: string) => void
  /** Offers "Reset to default" when given. */
  resetTo?: string
  hint?: React.ReactNode
  /** Instructions shown between the label and the box, where they are read before typing. */
  description?: React.ReactNode
  unknownVariableNote?: string
  /** Read only access: the wording and its checks, and no control that changes it. */
  readOnly?: boolean
}) {
  const field = React.useRef<HTMLTextAreaElement>(null)

  const template = React.useMemo(() => parseMessageTemplate(value), [value])
  const longest = longestFixedLength(template)
  const rawLength = Array.from(value).length
  const filledIn = template.valid && variablesUsed(template).length > 0
  const hasConditions = template.valid && template.conditions.length > 0
  const remaining = MESSAGE_LIMIT - longest
  const overLimit = remaining < 0
  const rawTooLong = rawLength > MAX_RAW_MESSAGE_LENGTH

  // Checked only once a query has told us what it returns.
  const fieldErrors = React.useMemo(
    () => (template.valid && variables.length > 0 ? unknownFieldErrors(template, variables) : []),
    [template, variables],
  )

  const unknown = React.useMemo(() => {
    if (variables.length === 0) return []
    const tested = template.conditions.map((condition) => condition.field)
    return messageVariables(value).filter(
      (name) => !variables.includes(name) && !tested.includes(name),
    )
  }, [value, variables, template])

  // Accented letters such as á and í, and Word's curly quotes, fall outside the
  // standard texting alphabet. Said plainly; estimating the cost is out of scope.
  const unusual = React.useMemo(() => nonGsmCharacters(value), [value])

  const insert = (text: string) => insertAtCursor(field.current, value, text, onChange)

  return (
    <div className="space-y-2">
      <label htmlFor={id}>{label}</label>
      {description && (
        <p id={`${id}_description`} className="text-xs text-ink-muted">
          {description}
        </p>
      )}
      <Textarea
        id={id}
        ref={field}
        rows={3}
        value={value}
        readOnly={readOnly}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={
          Boolean(error) || overLimit || !template.valid || fieldErrors.length > 0 || rawTooLong
        }
        aria-describedby={description ? `${id}_description ${id}_counter` : `${id}_counter`}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        {hint ? <p className="text-xs text-ink-muted">{hint}</p> : <span />}

        <p
          id={`${id}_counter`}
          className={overLimit ? "text-xs text-danger-display" : "text-xs text-ink-muted"}
        >
          {filledIn ? "Fixed text " : hasConditions ? "Longest version " : ""}
          {longest} / {MESSAGE_LIMIT}
          {overLimit ? ` — ${Math.abs(remaining)} over` : ""}
        </p>
      </div>

      {filledIn && !overLimit && (
        <p className="text-xs text-ink-muted">
          Names and other details are added for each member. A text that comes out
          over {MESSAGE_LIMIT} characters isn’t sent, and the run lists it as too long.
        </p>
      )}

      {variables.length > 0 && !readOnly && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-ink-muted">Insert:</span>
          {variables.map((variable) => (
            <button
              key={variable}
              type="button"
              className="toggle-button"
              onClick={() => insert(`{${variable}}`)}
            >
              {`{${variable}}`}
            </button>
          ))}
          <InsertConditionDialog variables={variables} onInsert={insert} />
        </div>
      )}

      {!template.valid && (
        <p className="text-xs text-danger-display">{template.error}</p>
      )}

      {fieldErrors.map((sentence) => (
        <p key={sentence} className="text-xs text-danger-display">
          {sentence}
        </p>
      ))}

      {rawTooLong && (
        <p className="text-xs text-danger-display">
          This message is {rawLength} characters including the condition wording.
          The most it can hold is {MAX_RAW_MESSAGE_LENGTH.toLocaleString()}.
        </p>
      )}

      {unknown.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-signal-display">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {unknown.map((name) => `{${name}}`).join(", ")} {unknownVariableNote}
          </span>
        </p>
      )}

      {error && <p className="text-xs text-danger-display">{error}</p>}

      {unusual.length > 0 && (
        <p className="text-xs text-ink-muted">
          {unusual.slice(0, 8).join(" ")}{" "}
          {unusual.length === 1 ? "isn’t" : "aren’t"} in the standard texting
          alphabet, so this text goes out in shorter parts — 70 characters each
          instead of 160 — and can cost more to send.
        </p>
      )}

      {resetTo !== undefined && !readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={value === resetTo}
          onClick={() => onChange(resetTo)}
        >
          {value === resetTo ? "Using the default wording" : "Reset to default"}
        </Button>
      )}
    </div>
  )
}
